<template>
  <div class="call-logs-quick-filter">
    <Tabs
      v-model="selectedGroupBy"
      @before-change="onBeforeTabChange"
      @change="loadData"
    >
      <Tab
        v-for="(column, idx) in tabGroupings"
        :key="idx"
        :title="column.displayName"
      />

      <Tab v-if="!hideTags">
        <template slot="title">
          <span
            ref="tagsTab"
            @click="onTabClick"
          >
            Tags
            <i class="m-l-5 fa fa-angle-down" />
          </span>
        </template>
      </Tab>
    </Tabs>

    <popover
      v-if="!hideTags"
      v-model="showTagsPopover"
      custom-class="breakdown-legend__popover"
      placement="bottom"
      trigger="manual"
      :target="$refs.tagsTab"
      @show="tagsPopoverVisible = true"
      @hide="tagsPopoverVisible = false"
    >
      <template slot="popover">
        <InsightsGroupByDropdown
          :available-group-bys="tagGroupBys"
          :trigger-element="$refs.tagsTab && $refs.tagsTab.parentNode.parentNode"
          :focus-dropdown="tagsPopoverVisible"
          class="insights-group-by-dropdown"
          @input="onGroupByChange"
          @blur="showTagsPopover = false"
        />
      </template>
    </popover>
    
    <div>
      <div
        v-if="showPartialDataSetMessage"
        class="reporting-grid-notification--partial bg-info m-t-2"
        @click="hidePartialDatasetNotification"
      >
        <i class="fa fa-times pull-right m-t-3 m-l-5" />

        <i class="fa fa-info m-t-3 m-r-5" />
        <span v-t="'PARTIAL_DATA_SET_MESSAGE'" />
      </div>
      <AgGridVue
        class="ag-grid--custom ag-grid--call-logs ag-grid--call-logs-quick-insights ag-theme-balham-dark"
        :row-data="rowData.allRecords"
        :default-col-def="defaultColDef"
        :column-defs="columnDefs"
        :column-types="colTypes"
        :group-header-height="0"
        :suppress-context-menu="true"
        :suppress-movable-columns="true"
        :overlay-no-rows-template="overlayNoRowsTemplate"
        :suppress-animation-frame="true"
        :suppress-column-move-animation="true"
        :suppress-column-state-events="true"
        :suppress-property-names-check="true"
        :tooltip-show-delay="0"
        dom-layout="autoHeight"
        @grid-ready="onGridReady"
        @cellClicked="onCellClicked"
        @sortChanged="onSortChange"
        @columnResized="onColumnResized"
      />
    </div>
  </div>
</template>

<script>
import { AgGridVue } from '@ag-grid-community/vue';
import { debounce } from 'lodash';

import { isMobile } from '@/core/utilities';
import InsightsGroupByDropdown from '../../ReportingInsights/InsightsGroupByDropdown';
import { columnTypes, numericTypesForCallLogSummary, isWhitespace } from '@/modules/reporting/components/AgGridUtils';
import partialDataSetMixin from '@/modules/reporting/mixins/partialDataSetMixin';
import FilterableCellRenderer from './FilterableCellRenderer.vue';

let gridApi, gridColumnApi;

const GROUP_BY_DATE_COL_ID = 'datesByYearMonthDay';

const INT_SORT_DATE_TAGS_SET = new Set([
  'tag:Time:Hour',
  'tag:Time:Minute',
  'tag:Time:Second',
  'tag:Time:UTCHour',
  'tag:Time:UTCMinute',
  'tag:Time:UTCSecond',
  'tag:Date:Day',
  'tag:Date:Month',
  'tag:Date:Year',
  'tag:Date:UTCDay',
  'tag:Date:UTCMonth',
  'tag:Date:UTCYear'
]);

const columnToFilterMapping = {
  'callCount': {
    field: null,
    value: null,
    excludeTotalsRow: true
  },
  'connectedCallCount': {
    field: 'hasConnected',
    value: 'yes',
    blockFilterForRoles: ['buyer']
  },
  'completedCalls': {
    field: 'isIncomplete',
    value: 'no',
    blockFilterForRoles: ['buyer']
  },
  'payoutCount': {
    field: 'hasPayout',
    value: 'yes'
  },
  'convertedCalls': {
    field: 'hasConverted',
    value: 'yes',
    blockFilterForRoles: ['buyer']
  },
  'duplicateCalls': {
    field: 'isDuplicate',
    value: 'yes'
  },
  'incompleteCalls': {
    field: 'isIncomplete',
    value: 'yes'
  },
  'nonConnectedCallCount': {
    field: 'hasConnected',
    value: 'no'
  },
  'blockedCalls': {
    field: 'wasBlocked',
    value: 'yes'
  },
};


const defaultTabForRoles = {
  'admin': 'campaignName',
  'user': 'campaignName',
  'buyer': 'buyerTargetName',
  'publisher': 'campaignName'
};

function checkIfSortColumnIsDisplayed(displayedColumns) {
  return displayedColumns.some(column => {
    return !!column.sort;
  });
}

function pinnedRowCellRenderer() {
  return `<div class="ag-grid-pinned-row ag-grid-pinned-row--totals">Totals</div>`;
}

function getDefaultColDef(role) {
  return {
    cellClass: getCellClass,
    suppressMenu: true,
    flex: 1,
    cellRendererFramework: 'FilterableCellRenderer',
    cellRendererParams: {
      role,
      columnToFilterMapping
    }
  }; 
}

function getCellClass(params) { 
  if (params) {
    const classes = [];

    if (params.colDef) {
      if (numericTypesForCallLogSummary[params.colDef.type]) {
        classes.push('ag-right-aligned-cell');
      }
    }

    if (isWhitespace(params.value)) {
      classes.push('ag-grid--blanks');
    }

    return classes;
  }
}

function clearSortInColumns(columns) {
  return columns.map(col => { 
    return { ...col, sort: null, sortIndex: null }; 
  });
}

export default {
  name: 'ReportingCallLogsQuickInsights',
  components: {
    AgGridVue,
    InsightsGroupByDropdown,
    FilterableCellRenderer,
  },

  mixins: [
    partialDataSetMixin
  ],

  props: {
    rowData: {
      type: Object,
      default() {
        return {};
      }
    },

    colDefsLoadedFromApi: {
      type: Boolean,
      default: false
    },

    columnDefs: {
      type: Array,
      default() {
        return [];
      }
    },

    tabGroupings: {
      type: Array,
      default() {
        return [];
      }
    },

    columnState: {
      type: Array,
      default() {
        return [];
      }
    },

    refreshGridData: {
      type: Number,
      default: 0
    },

    showLoader: {
      type: Boolean,
      default: false
    },

    tagGroupBys: {
      type: Object,
      default() {
        return {};
      }
    },

    hideTags: {
      type: Boolean,
      default: false
    },

    selectTabGrouping: {
      type: String,
      default: ''
    },
    
    role: {
      type: String,
      default: ''
    },

    exportQuickInsightsTrigger: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      showTagsPopover: false,
      tagsPopoverVisible: false,
      selectedTag: null,
      selectedGroupBy: 0,
      colTypes: Object.freeze({
        ...columnTypes,
      }),
      overlayNoRowsTemplate: 'No data to display. Try selecting a different date range or filter settings.'
    };
  },

  computed: {
    defaultColDef() {
      return getDefaultColDef(this.role);
    }
  },

  watch: {
    columnDefs(newValue) {
      if (newValue && gridColumnApi && this.colDefsLoadedFromApi) {
        this.$nextTick(() => {
          this.setGridColumnState(this.columnState);
          
          if (!this.requestedInitialDataLoad) {
            this.loadData(this.selectedGroupBy, true);
          }
        });
      }
    },

    'rowData.totalsRecord'(newValue) {
      if (newValue && gridApi) {
        gridApi.setPinnedBottomRowData(newValue);
      }
    },

    'rowData.partialResult'(newValue) {
      if (newValue) {
        this.showPartialDatasetNotification();
      } else {
        this.hidePartialDatasetNotification();
      }
    },

    tabGroupings: {
      immediate: true,
      handler(newValue, oldValue) {
        if (this.selectTabGrouping.startsWith('tag:') && this.role !== 'buyer') {
          this.setTagTabGrouping();
          return;
        }

        let tabIndex = newValue.findIndex(col => col.colId === this.selectTabGrouping && !col.hide);

        if (tabIndex < 0) {
          tabIndex = newValue.findIndex(col => col.colId === defaultTabForRoles[this.role] && !col.hide);
        }

        this.selectedGroupBy = tabIndex;

        // Load data when initial tabGroupings changed
        if (!oldValue?.length && newValue?.length) {
          this.loadData(this.selectedGroupBy);
        }
      }
    },

    selectTabGrouping: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          if (this.selectTabGrouping.startsWith('tag:') && this.role !== 'buyer') {
            this.setTagTabGrouping();
            return;
          }

          let tabIndex = this.tabGroupings.findIndex(col => col.colId === newValue);
          this.selectedGroupBy = tabIndex != undefined ? tabIndex : this.selectedGroupBy;
        }
      }
    },

    columnState(newValue) {
      if (this.colDefsLoadedFromApi) {
        this.setGridColumnState(newValue);
      }
    },

    refreshGridData() {
      this.loadData(this.selectedGroupBy, true);
    },

    showLoader(newValue) {
      if (!gridApi) {
        return;
      }

      if (newValue) {
        gridApi.showLoadingOverlay();
      } else {
        gridApi.hideOverlay();
      }
    },
    
    exportQuickInsightsTrigger() {
      gridApi?.exportDataAsCsv({
        fileName: 'call-summary.csv',
        sheetName: 'Call Log Summary'
      });
    },
  },

  created () {
    this.requestedInitialDataLoad = false;
    this.selectedTag = null;
  },

  beforeDestroy () {
    gridApi = null;
    gridColumnApi = null;
  },

  methods: {
    getTagFromFieldName(fieldName) {
      let [tagText, tagType, tagName] = fieldName.split(':');

      return {
        displayName: `${tagType}:${tagName}`,
        field: fieldName,
        originalTag: {
          tagName,
          tagType
        }
      };
    },

    setTagTabGrouping() {
      this.selectedTag = this.getTagFromFieldName(this.selectTabGrouping);
      this.selectedGroupBy = this.tabGroupings.length;
    },

    onGridReady(params) {
      gridApi = params.api;
      gridColumnApi = params.columnApi;

      if (this.colDefsLoadedFromApi) {
        this.setGridColumnState(this.columnState);
      }

      if (!this.requestedInitialDataLoad) {
        this.loadData(this.selectedGroupBy, !this.tabGroupings[this.selectedGroupBy] && this.selectedTag);
      }
    },

    setGridColumnState(columnState) {
      if (gridColumnApi) {
        gridColumnApi.applyColumnState({ 
          state: columnState,
          applyOrder: true,
          defaultState: {
            hide: true
          }
        });
      }
    },

    loadData(tabIndex, onTagChange, onSortChange) {
      if (!gridColumnApi 
          || !this.tabGroupings?.length 
          || !(this.columnDefs?.length > 1)
          || (!this.tabGroupings[tabIndex] && !onTagChange)
          || !this.colDefsLoadedFromApi) {
        return;
      }

      let currentColumnState = gridColumnApi?.getColumnState()?.filter(col => !col.hide);
      let newColumnState = [...currentColumnState];

      let groupingColumn = {
        sortable: true,
        pinnedRowCellRenderer,
        resizable: true,
        minWidth: 0,
      };

      let groupingColId = '';


      if (!this.tabGroupings[tabIndex] && this.selectedTag) {
        groupingColId = this.selectedTag.field;

        groupingColumn = {
          ...groupingColumn,
          colId: this.selectedTag.field,
          field: this.selectedTag.field,
          headerName: this.selectedTag.displayName,
          headerTooltip: this.selectedTag.displayName,
          enableFilter: true,
          filterGroupName: undefined,
          originalTag: this.selectedTag.originalTag,
          type: 'tag'
        };

        // Add column type of integer to tags related to Time and Date that should be seen as numbers, not strings.
        if (INT_SORT_DATE_TAGS_SET.has(groupingColId)) {
          groupingColumn.type = 'integer';
        }

      } else if (this.tabGroupings[tabIndex]) {
        this.selectedTag = null;
        let selectedGrouping = this.tabGroupings[tabIndex];
        groupingColId = this.tabGroupings[tabIndex].colId;

        groupingColumn = {
          ...groupingColumn,
          colId: selectedGrouping.colId,
          field: selectedGrouping.colId,
          headerName: selectedGrouping.displayName,
          headerTooltip: selectedGrouping.displayName,
          enableFilter: selectedGrouping.enableFilter,
          filterGroupName: selectedGrouping.filterGroupName,
          type: selectedGrouping.type,
          valueFormatter: selectedGrouping.valueFormatter,
        };
      } else {
        return;
      }

      if (!onSortChange && !checkIfSortColumnIsDisplayed(gridColumnApi.getAllDisplayedColumns().slice(1))) {
        newColumnState = clearSortInColumns(newColumnState);
        let incomingCallCountColumnState = newColumnState.find(col => col.colId === 'callCount');
        
        if (incomingCallCountColumnState) {
          incomingCallCountColumnState.sort = 'desc';
        }
      }
      
      if (!onSortChange && groupingColId === GROUP_BY_DATE_COL_ID) {
        newColumnState = clearSortInColumns(newColumnState);
      }

      this.requestedInitialDataLoad = true;

      this.$emit('changeTabGrouping', {groupingColId, isMobile});
      
      // Hack to set sort model on initial load, grid wouldn't change the sort model without timeout on initial load
      // getAllDisplayedColumns() returns stale cols if not in setTimeout when changing viewAs roles
      setTimeout(() => {
        // This is reset to null when accounts are switched (admin to admin)
        if (!gridColumnApi) {
          return;
        }

        gridColumnApi.applyColumnState({
          state: newColumnState,
          defaultState: {
            sort: null
          }
        });

        this.$emit('loadData', {
          groupingColumn,
          columns: gridColumnApi.getAllDisplayedColumns().filter(col => !col.pinned && col.colId != groupingColId)
            .map(col => {
              return {
                ...col,
                colDef: gridColumnApi.getColumn(col.colId)
              };
            }),
        });

      }, 0);
    },

    onGroupByChange(tag) {
      this.showTagsPopover = false;

      this.selectedGroupBy = this.tabGroupings.length;

      this.selectedTag = tag || this.selectedTag;

      this.loadData(this.selectedGroupBy, true);
    },

    onBeforeTabChange(indexFrom, indexTo, done) {
      if (indexTo === this.tabGroupings.length) {
        this.showTagsPopover = !this.showTagsPopover;
        done(1);
        return;
      }

      done();
    },

    onTabClick() {
      if (this.selectedGroupBy === this.tabGroupings.length) {
        this.showTagsPopover = !this.showTagsPopover;
      }
    },

    onSortChange() {
      if (this.rowData.partialResult) {
        this.loadData(this.selectedGroupBy, true, true);
      }
    },

    onCellClicked(e) {
      const cssClassList = e.event.target.classList;

      if (cssClassList.contains('filterable-cell')) {
        let value = e.value;
        const columnsFilter = [];
        const colId = e.column.colId;

        // Filter for the pinned column as well
        if (!e.column.isLockPinned() && e.rowPinned !== 'bottom') {
          let pinnedColumn = e.columnApi.getAllDisplayedColumns()[0];
          const pinnedColId = pinnedColumn.colId;
          // Don't add group by dates filter
          if(pinnedColId === GROUP_BY_DATE_COL_ID) {
            this.filterByDay(e.data[pinnedColId]);
          } else {
            columnsFilter.push({
              column: pinnedColId,
              value: e.data[pinnedColId],
              filterGroupName: pinnedColumn.colDef.filterGroupName,
              type: pinnedColumn.colDef.type,
              isTag: !!pinnedColumn.colDef.originalTag,
              originalTag: pinnedColumn.colDef.originalTag,
            });
          }
        }

        if (columnToFilterMapping[colId]) {
          value = columnToFilterMapping[colId].value;
        }

        if (value) {
          if(colId === GROUP_BY_DATE_COL_ID) {
            this.filterByDay(e.data[colId]);
          } else {
            columnsFilter.push({
              column: columnToFilterMapping[colId] ? columnToFilterMapping[colId].field : colId,
              value,
              filterGroupName: e.column.colDef.filterGroupName,
              type: e.colDef.type,
              isTag: !!e.colDef.originalTag,
              originalTag: e.colDef.originalTag,
            });
          }
        }

        this.$emit('filterByColumn', columnsFilter);
      }
    },

    filterByDay(day) {
      this.$emit('filterByDate', { reportStartInEpoch: day, reportEndInEpoch: day, timeGroupInterval: 'day' });
    },

    onColumnResized: debounce(function onColumnResized() {
      this.onGridStateChange();
    }, 1000),

    onGridStateChange() {
      if (this.colDefsLoadedFromApi) {
        this.$emit('gridStateChange', { columnState: this.getColumnState(), isMobile });
      }
    },

    getColumnState() {
      return gridColumnApi?.getColumnState().filter(col => !col.hide && col.pinned);
    },
  },
};
</script>

<style lang="scss">
.call-logs-quick-filter {
  .ag-grid--call-logs-quick-insights {
    height: initial;

    .ag-overlay-no-rows-wrapper {
      &.ag-layout-auto-height {
        align-items: unset;
        margin-top: 15px;
      }
    }

    @media (max-width: 1536px) {
      .ag-header-cell-text {
        font-size: 0.85em;
      }

      .ag-cell-value {
        font-size: 0.85em;
      }
    }
  }

  &>section {
    ul[role="tablist"] {
      a[role="tab"] {
        padding: 10px 30px;
        // padding-top: 11px;
      }

      li[role="presentation"].active a[role="tab"] {
        padding-top: 8px;
        padding-bottom: 8px;
      }
    }

    .tab-content {
      margin-top:0;
    }
  }
}
</style>